import React from 'react';
import { Shield, CheckCircle, AlertCircle, Clock, RefreshCw, ExternalLink } from 'lucide-react';
import ListingImageWithStoreLogo from './ListingImageWithStoreLogo';

const EscrowListItem = ({ 
  escrow, 
  user, 
  onClick, 
  formatRelativeTime, 
  formatPrice 
}) => {
  const getEscrowType = (escrow, userId) => {
    return escrow.buyer_id === userId 
      ? { label: 'You paid', color: 'text-blue-600', icon: '↓' }
      : { label: 'You received', color: 'text-green-600', icon: '↑' };
  };

  const getOtherPartyName = (escrow, userId) => {
    if (escrow.buyer_id === userId) {
      // User is buyer, show seller info
      // Prioritize store name, then personal name, then generic
      return escrow.seller_store_name?.trim() || 
             `${escrow.seller_first_name || ''} ${escrow.seller_last_name || ''}`.trim() || 
             'Seller';
    } else {
      // User is seller, show buyer info
      return `${escrow.buyer_first_name || ''} ${escrow.buyer_last_name || ''}`.trim() || 
             'Buyer';
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { 
        color: 'bg-yellow-50 text-yellow-700 border-yellow-200', 
        icon: <Clock className="w-3 h-3" /> 
      },
      held: { 
        color: 'bg-green-50 text-green-700 border-green-200', 
        icon: <Shield className="w-3 h-3" /> 
      },
      released: { 
        color: 'bg-blue-50 text-blue-700 border-blue-200', 
        icon: <CheckCircle className="w-3 h-3" /> 
      },
      disputed: { 
        color: 'bg-orange-50 text-orange-700 border-orange-200', 
        icon: <AlertCircle className="w-3 h-3" /> 
      },
      refunded: { 
        color: 'bg-purple-50 text-purple-700 border-purple-200', 
        icon: <RefreshCw className="w-3 h-3" /> 
      }
    };
    return configs[status] || { 
      color: 'bg-gray-50 text-gray-700 border-gray-200', 
      icon: <Shield className="w-3 h-3" /> 
    };
  };

  const escrowType = getEscrowType(escrow, user.user_id);
  const otherPartyName = getOtherPartyName(escrow, user.user_id);
  const statusConfig = getStatusConfig(escrow.status);

  return (
    <div
      onClick={onClick}
      className="group cursor-pointer hover:bg-gray-50 transition-all duration-200"
    >
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-4">
          {/* Combined Image */}
          <ListingImageWithStoreLogo
            listingImage={escrow.listing_image}
            storeLogo={escrow.seller_store_logo}
            profilePicture={escrow.seller_profile_picture}
            storeName={escrow.seller_first_name}
            size="md"
          />

          {/* Escrow Details */}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-semibold text-gray-900 group-hover:text-[#ff660e] transition-colors">
                  {escrow.listing_title || 'Transaction'}
                </h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className={`text-sm ${escrowType.color} font-medium flex items-center gap-1`}>
                    <span className="text-base">{escrowType.icon}</span>
                    {escrowType.label}
                  </span>
                  <span className="text-sm text-gray-500">•</span>
                  <span className="text-sm text-gray-600">
                    with {otherPartyName}
                  </span>
                </div>
              </div>
              
              <div className="text-right">
                <p className="font-bold text-black text-lg">
                  {formatPrice(escrow.amount)}
                </p>
                <p className="text-sm text-gray-500 mt-1 flex items-center justify-end gap-1">
                  <span className="w-3 h-3" />
                  {formatRelativeTime(escrow.created_at)}
                </p>
              </div>
            </div>

            {/* Status and Actions */}
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-2 border ${statusConfig.color}`}>
                  {statusConfig.icon}
                  {escrow.status.charAt(0).toUpperCase() + escrow.status.slice(1)}
                </span>
                
                <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded">
                  ID: {escrow.escrow_id}
                </span>
              </div>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
                className="text-sm text-[#ff660e] hover:text-[#ff5500] font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"
              >
                View Details
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EscrowListItem;